import React from "react"
import { Link } from "react-router-dom"
import { ArrowRight, HardHat, Briefcase, HeartPulse, Factory, ShoppingBag } from "lucide-react"
import SEO from "../components/SEO.jsx"
import PageHero from "../components/page/PageHero.jsx"
import SectionHeading from "../components/home/SectionHeading.jsx"
import FinalCTA from "../components/home/FinalCTA.jsx"

// Links point only at existing landing pages in pages/landing/landingPages.js.
// Where near-duplicate pages exist, one is linked to avoid cannibalisation.
const SECTORS = [
  {
    icon: HardHat,
    title: "Construction & specialist trades",
    text: "Websites, local search and Google Ads that showcase completed work and win better-quality project enquiries.",
    links: [
      { to: "/websites-for-builders", label: "Builders" },
      { to: "/websites-for-tradies", label: "Tradies" },
      { to: "/websites-for-plumbers", label: "Plumbers" },
      { to: "/websites-for-electricians", label: "Electricians" },
      { to: "/websites-for-landscapers", label: "Landscapers" },
      { to: "/websites-for-automatic-gates", label: "Automatic gate installers" },
      { to: "/websites-for-pest-control", label: "Pest control" },
    ],
  },
  {
    icon: Briefcase,
    title: "Professional services",
    text: "Credible, clearly structured websites and search visibility for firms where trust drives every enquiry.",
    links: [
      { to: "/websites-for-lawyers", label: "Lawyers" },
      { to: "/websites-for-accounting-firms", label: "Accounting firms" },
      { to: "/websites-for-financial-advisers", label: "Financial advisers" },
      { to: "/websites-for-real-estate-agents", label: "Real estate agents" },
      { to: "/websites-for-it-support", label: "IT support" },
      { to: "/websites-for-marketing-agencies", label: "Marketing agencies" },
    ],
  },
  {
    icon: HeartPulse,
    title: "Health & allied practices",
    text: "Calm, carefully worded websites and local visibility that make it easy for patients to understand services and book.",
    links: [
      { to: "/websites-for-chiropractors", label: "Chiropractors" },
      { to: "/websites-for-physiotherapists", label: "Physiotherapists" },
      { to: "/websites-for-dentists", label: "Dentists" },
      { to: "/websites-for-medical-centres", label: "Medical centres" },
      { to: "/websites-for-psychologists", label: "Psychologists" },
    ],
  },
]

const OTHER_SECTORS = [
  {
    icon: Factory,
    title: "Manufacturing & B2B",
    text: "Websites that explain capability clearly to trade and business buyers, supported by search visibility and integrations with quoting, CRM and administration systems.",
    link: { to: "/business-integrations", label: "Business integrations" },
  },
  {
    icon: ShoppingBag,
    title: "Premium retail & e-commerce",
    text: "Online stores and product information connected to your operations, AI assistants that help customers find the right product, and the search visibility to reach the right customers.",
    link: { to: "/business-integrations", label: "E-commerce and integrations" },
  },
]

export default function Industries() {
  return (
    <div className="bg-white text-brand-text">
      <SEO
        title="Industries We Work With | 2merative"
        description="Digital growth for established businesses in construction and trades, professional services, health practices, manufacturing, B2B and premium retail."
        path="/industries"
        image="/og-image.png"
      />

      <PageHero
        eyebrow="Industries"
        title="Digital growth for established industries"
        lead="Every sector wins work differently. We shape websites, search visibility and campaigns around how your customers choose who to contact."
      />

      <section aria-labelledby="sectors-heading" className="py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <SectionHeading
            id="sectors-heading"
            eyebrow="Sectors"
            title="Where we work most often"
          />

          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {SECTORS.map(({ icon: Icon, title, text, links }, index) => (
              <article
                key={title}
                aria-labelledby={`sector-${index}-heading`}
                className="flex flex-col rounded-xl2 border border-brand-border bg-white p-7 shadow-card"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-primarySoft text-brand-primary">
                  <Icon aria-hidden="true" className="h-6 w-6" />
                </span>
                <h3 id={`sector-${index}-heading`} className="mt-5 text-xl font-semibold text-brand-heading">
                  {title}
                </h3>
                <p className="mt-3 leading-relaxed text-brand-textSoft">{text}</p>
                <ul className="mt-5 space-y-1 border-t border-brand-border pt-4">
                  {links.map((link) => (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        className="flex min-h-[40px] items-center justify-between gap-3 rounded-lg text-brand-text hover:text-brand-primary"
                      >
                        {link.label}
                        <ArrowRight aria-hidden="true" className="h-4 w-4 shrink-0" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section aria-labelledby="other-sectors-heading" className="bg-brand-bg py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <SectionHeading
            id="other-sectors-heading"
            title="Also working with"
            lead="Not seeing your industry? The same principles apply to most established businesses. Tell us how yours works."
          />

          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {OTHER_SECTORS.map(({ icon: Icon, title, text, link }) => (
              <article key={title} className="rounded-xl2 border border-brand-border bg-white p-7 shadow-card">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-primarySoft text-brand-primary">
                  <Icon aria-hidden="true" className="h-6 w-6" />
                </span>
                <h3 className="mt-5 text-xl font-semibold text-brand-heading">{title}</h3>
                <p className="mt-3 leading-relaxed text-brand-textSoft">{text}</p>
                <Link
                  to={link.to}
                  className="mt-5 inline-flex items-center gap-2 font-semibold text-brand-primary hover:underline"
                >
                  {link.label}
                  <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  )
}
